import { SyntheticEvent, useState } from 'react';

import { Alert, Button, Form, Input } from 'antd';
import { List, Typography } from 'antd';
import { MaskedInput } from 'antd-mask-input';
import { isEmpty } from 'lodash';
import { TCustomer } from 'types/customer';

import { AfterCodeActions } from '@api/enums/afterCodeActions';
import { CustomerProfileResp } from '@api/responseModels/customer/customerProfileResponse';
import { InvitationResp } from '@api/responseModels/invitation/invitationResponse';
import { ConnectionResp } from '@api/responseModels/security/connectionResponse';
import { CRMAPIManager } from '@classes/crmApiManager';
import { SettingsManager } from '@classes/settingsManager';
import RenderIf from '@common/RenderIf';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { buildFallbackCustomerName } from '@utils/buildFallbackCustomerName';
import { getUserFullName } from '@utils/getUserFullName';
import { handleRussianPhonePaste, useRussianPhoneInput } from '@utils/russianPhoneInput';

/** Отступ между полями вместо штатных 24px antd */
const FIELD_STYLE = { marginBottom: 8 };

const ENTITY_TYPE = {
    crm: 'Компания',
    user: 'Пользователь',
};

type TProps = {
    afterCustomerCreate: (customer: TCustomer) => void;
};

export const CreateCustomer = ({ afterCustomerCreate }: TProps): JSX.Element => {
    const [lastDialogError, setLastDialogError] = useState<string>(null);
    const phoneInputRef = useRussianPhoneInput();
    const [phoneForm] = Form.useForm();
    const [workerExists, setWorkerExists] = useState<boolean>(null);
    const [numberValid, setNumberValid] = useState<boolean>(false);
    const [numberValue, setNumberValue] = useState<string>('');
    const [nameValue, setNameValue] = useState<string>('');
    const phoneMask = /^[0-9]{10}$/;
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [existsEntities, setExistsEntities] = useState([]);
    const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
    const [isCreating, setIsCreating] = useState<boolean>(false);

    async function validatePhone(): Promise<boolean> {
        let result = true;
        await phoneForm.validateFields().catch(() => {
            result = false;
        });
        return result;
    }

    async function checkIfExists(): Promise<boolean> {
        try {
            const ch = await CRMAPIManager.request<ConnectionResp>(async (api) => {
                return await api.connection(numberValue);
            });
            if (ch.errorMessages) throw new Error(ch.errorMessages[0]);
            return ch.data.require_method == AfterCodeActions.login;
        } catch (err) {
            setLastDialogError(err.message);
            return null;
        }
    }

    async function finishCreation(customerData: TCustomer) {
        setWorkerExists(null);
        setNumberValue('');
        setNameValue('');
        phoneForm.setFieldValue('phone', '');
        afterCustomerCreate(customerData);
    }

    async function addNewWorker(): Promise<void> {
        if (!(await validatePhone())) return;

        setIsCreating(true);
        try {
            const check = workerExists ?? (await checkIfExists());
            if (workerExists == null) {
                setWorkerExists(check);
            }
            const creds = SettingsManager.getConnectionCredentials();
            const customerName = nameValue.trim();

            if (selectedEntity?.entity_type === 'crm') {
                const customer = await CRMAPIManager.request<CustomerProfileResp>(async (api) => {
                    return await api.saveCustomerProfile(creds.crmID, {
                        name:
                            customerName ||
                            selectedEntity?.data?.organization_name ||
                            'Без имени',
                        discount: 0,
                        type: 'crm',
                        receive_crm_id: selectedEntity?.id,
                        crm_id: creds.crmID,
                        balance: 0,
                        phone: numberValue,
                    });
                });
                const customerData = customer.data?.data;

                await finishCreation(customerData);
            } else {
                if (check == true) {
                    const inv = await CRMAPIManager.request<InvitationResp>(async (api) => {
                        return await api.inviteRegisterUser(creds.crmID, numberValue, 'customer');
                    });
                    if (inv.errorMessages) throw new Error(inv.errorMessages[0]);

                    // Получаем данные созданного клиента. `data.id` — это id приглашения,
                    // профиль клиента лежит в `data.customer`
                    const customerResp = await CRMAPIManager.request<CustomerProfileResp>(
                        async (api) => {
                            return await api.getCustomerProfile(
                                inv.data?.data?.customer?.id,
                                creds.crmID
                            );
                        }
                    );
                    await finishCreation(customerResp.data?.data);
                } else {
                    const resp = await CRMAPIManager.request<InvitationResp>(async (api) => {
                        return await api.inviteNotRegisterUser(
                            creds.crmID,
                            {
                                name: customerName || buildFallbackCustomerName(numberValue),
                                phone: numberValue,
                            } as any,
                            'customer'
                        );
                    });

                    // Получаем данные созданного клиента
                    const customerResp = await CRMAPIManager.request<CustomerProfileResp>(
                        async (api) => {
                            return await api.getCustomerProfile(
                                resp.data?.data?.customer?.id,
                                creds.crmID
                            );
                        }
                    );
                    await finishCreation(customerResp.data?.data);
                }
            }
        } catch (err) {
            setLastDialogError(err.message);
        } finally {
            setIsCreating(false);
        }
    }

    function onNumberChange(e: SyntheticEvent & { maskedValue: string; unmaskedValue: string }) {
        const newValue = e.unmaskedValue;
        if (newValue && phoneMask.test(newValue)) {
            setNumberValid(true);
            searchByNumber(newValue);
        } else {
            setNumberValid(false);
        }
        setNumberValue(newValue);
    }

    const searchByNumber = async (number: string) => {
        try {
            setIsSearchLoading(true);
            const resp = await CRMAPIManager.request<null>(async (api) => {
                return await api.search({
                    term: number,
                    entityType: ['crm', 'user'],
                    search_attributes: ['phone'],
                    pagination: {
                        page: 1,
                        limit: 5,
                    },
                });
            });
            setExistsEntities(resp?.data?.data);
        } catch (err) {
            console.error(err);
        } finally {
            setIsSearchLoading(false);
        }
    };

    return (
        <>
            {lastDialogError && (
                <Alert
                    className="dialog-alert"
                    message={lastDialogError}
                    type="error"
                    closable
                    onClose={() => setLastDialogError(null)}
                />
            )}
            {/* Без подписей и с узкими отступами: на телефоне поля и «Добавить»
                должны быть под пальцем, а не уезжать вниз экрана */}
            <Form form={phoneForm} layout="vertical" scrollToFirstError>
                <Form.Item
                    style={FIELD_STYLE}
                    rules={[
                        {
                            required: true,
                            message: 'Пожалуйста, введите номер телефона',
                        },
                    ]}
                >
                    <MaskedInput
                        ref={phoneInputRef}
                        name="phone"
                        aria-label="Телефон"
                        disabled={lastDialogError !== null || isSearchLoading || isCreating}
                        mask="(000)-000-00-00"
                        onPaste={handleRussianPhonePaste}
                        onChange={onNumberChange}
                        placeholder="(___)-___-__-__"
                        prefix="+7"
                        value={numberValue}
                    />
                </Form.Item>
                {/* Имя необязательно: без него клиент создаётся под маской из
                    последних цифр телефона, как было до появления этого поля */}
                <Form.Item style={FIELD_STYLE}>
                    <Input
                        name="name"
                        aria-label="Имя"
                        disabled={lastDialogError !== null || isSearchLoading || isCreating}
                        onChange={(e) => setNameValue(e.target.value)}
                        placeholder="Имя (Необязательно)"
                        value={nameValue}
                        allowClear
                    />
                </Form.Item>
            </Form>
            {/* Пустой список antd показывает заглушку «Ничего не нашлось»: на этой
                вкладке клиента ещё только создают, и искать было нечего — сообщение
                выглядело как ошибка */}
            <RenderIf condition={!isEmpty(existsEntities)}>
                <List
                    dataSource={existsEntities}
                    renderItem={(item, index) => (
                        <List.Item>
                            <div
                                style={{
                                    display: 'flex',
                                    width: '100%',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography.Text>
                                    {index + 1}.{' '}
                                    {item.type === 'user'
                                        ? getUserFullName(item.data)
                                        : item.data?.organization_name}{' '}
                                    ({ENTITY_TYPE[item.type]})
                                </Typography.Text>
                                <Button
                                    type={
                                        selectedEntity?.id === item?.id &&
                                        selectedEntity?.type === item?.type
                                            ? 'primary'
                                            : 'default'
                                    }
                                    shape="circle"
                                    icon={<FontAwesomeIcon icon={faCheck} />}
                                    onClick={() => setSelectedEntity(item)}
                                />
                            </div>
                        </List.Item>
                    )}
                />
            </RenderIf>

            <Button
                className="round-button"
                key="submit"
                type="primary"
                disabled={!numberValid || isCreating}
                loading={isCreating}
                onClick={async () => await addNewWorker()}
            >
                Добавить
            </Button>
        </>
    );
};
